import { IconAlertTriangle } from "@tabler/icons-react";
import type React from "react";
import type { FC, ReactNode } from "react";
import { Dialog, Heading } from "react-aria-components";
import { tv } from "tailwind-variants";
import { Button, ButtonVariant } from "../Buttons/button.js";
import { Size } from "../helpers/helpers.js";
import { CenteredModal } from "./centeredModal.js";

interface ModalProps {
  isOpen: boolean;
  onCancel?: () => void;
  children?: React.ReactNode;
  size?: typeof Size.LARGE | typeof Size.SMALL;
}

export const SimpleModalVariant = {
  Standard: "STANDARD",
  Warning: "WARNING",
} as const;
export type SimpleModalVariant =
  (typeof SimpleModalVariant)[keyof typeof SimpleModalVariant];

interface SimpleModalProps extends ModalProps {
  variant: SimpleModalVariant;
  icon?: ReactNode;
  title: string;
  subtitle: string | ReactNode;
  confirmButtonText: string;
  onConfirm: () => void;
  isPrimaryButtonLoading?: boolean;
}

/** Sizes the shared modal frame supports. */
export const ModalSize = {
  Small: Size.SMALL,
  Large: Size.LARGE,
  Full: "full",
} as const;

export type ModalSize = (typeof ModalSize)[keyof typeof ModalSize];

/** Styles for the modal dialog itself. */
export const modal = tv({
  // The entering/exiting data attributes work when used with react-aria-components.
  // Tailwind's translate/scale utilities set the standalone `translate` and
  // `scale` properties, so transitioning `transform` would not animate them.
  base: "transform-gpu rounded-xl bg-card shadow-2xl ring-1 ring-gray-950/10 transition-[opacity,translate,scale] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] outline-none data-[entering]:translate-y-3 data-[entering]:scale-[0.985] data-[entering]:opacity-0 data-[exiting]:translate-y-2 data-[exiting]:scale-[0.985] data-[exiting]:opacity-0 motion-reduce:transition-none",
  variants: {
    size: {
      [ModalSize.Large]: "sm:my-8 sm:max-w-4xl",
      [ModalSize.Small]: "sm:my-8 sm:max-w-lg",
      // Fills the stage's gutter and manages its own scrolling, for modals that
      // are a screen rather than a dialog.
      [ModalSize.Full]: "h-full w-full overflow-hidden",
    },
  },
  defaultVariants: {
    size: ModalSize.Small,
  },
});

// TODO: Deprecate this in favor of either `SimpleModal` (for "simple" modals) or `CenteredModal` with `Dialog` (for complex modals)?
export const Modal: React.FC<ModalProps> = (props) => {
  const { isOpen, onCancel, size = Size.SMALL, children } = props;

  const onOpenChange = (newIsOpen: boolean) => {
    if (!newIsOpen) {
      onCancel?.();
    }
  };

  return (
    <CenteredModal
      size={size}
      isDismissable
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog className="px-4 pt-5 pb-4 text-left sm:p-6">{children}</Dialog>
    </CenteredModal>
  );
};

export const SimpleModal: FC<SimpleModalProps> = (props) => {
  const {
    isOpen,
    variant,
    icon,
    title,
    subtitle,
    confirmButtonText,
    onCancel,
    onConfirm,
    isPrimaryButtonLoading,
    size,
  } = props;

  const renderIcon = (): ReactNode => {
    if (variant === SimpleModalVariant.Warning) {
      return (
        <div className="m:mt-5 mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <IconAlertTriangle
            aria-hidden="true"
            className="h-6 w-6 text-destructive"
          />
        </div>
      );
    }

    if (!icon) return null;

    return <div className="mb-4">{icon}</div>;
  };

  const renderPrimaryButton = (): ReactNode => {
    if (variant === SimpleModalVariant.Warning)
      return (
        <Button
          isLoading={isPrimaryButtonLoading}
          onClick={onConfirm}
          variant={ButtonVariant.Warning}
        >
          {confirmButtonText}
        </Button>
      );

    return (
      <Button
        isLoading={isPrimaryButtonLoading}
        onClick={onConfirm}
        variant={ButtonVariant.Primary}
      >
        {confirmButtonText}
      </Button>
    );
  };

  const renderButtons = (): ReactNode => {
    if (onCancel)
      return (
        <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
          <Button onClick={onCancel} variant={ButtonVariant.Secondary}>
            Cancel
          </Button>
          {renderPrimaryButton()}
        </div>
      );

    // If no cancel handler, only render the primary button
    return (
      <div className="mt-5 flex justify-center sm:mt-6">
        {renderPrimaryButton()}
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onCancel={onCancel} size={size}>
      <>
        <div>
          {renderIcon()}
          <div className="text-center">
            <Heading
              slot="title"
              className="text-base leading-6 font-semibold text-foreground"
            >
              {title}
            </Heading>
            <div className="mt-2">
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            </div>
            {props.children}
          </div>
        </div>
        {renderButtons()}
      </>
    </Modal>
  );
};
